// CookieConsentBanner — bottom-fixed banner shown until the user
// records a consent choice. Two-step: brief overview with quick
// Accept/Reject + "Customize" that opens granular toggles.
//
// Persistence + freshness logic lives in src/lib/cookieConsent.ts.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  shouldShowBanner,
  acceptAll,
  rejectAll,
  saveConsent,
} from '@/lib/cookieConsent';

export function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    setOpen(shouldShowBanner());
  }, []);

  if (!open) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setOpen(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setOpen(false);
  };
  const handleSavePreferences = () => {
    saveConsent({ statistics, marketing });
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie-inställningar"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 bg-background border-2 border-border rounded-lg shadow-2xl p-4 animate-in slide-in-from-bottom"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Cookie className="w-5 h-5 text-primary" />
          <h2 className="font-semibold text-sm">Cookies</h2>
        </div>
        <button
          onClick={handleRejectAll}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Stäng — avvisa allt utom nödvändigt"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Vi använder nödvändiga cookies för session-hantering. Med ditt
        samtycke kan vi även mäta anonymiserad användarstatistik.
        Läs mer i vår <Link to="/privacy" className="underline">integritetspolicy</Link>.
      </p>

      {customize && (
        <div className="space-y-3 p-3 rounded-md bg-muted/40 mb-3 text-xs">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-medium">Nödvändiga</Label>
              <p className="text-[10px] text-muted-foreground">Session, säkerhet, språk. Alltid på.</p>
            </div>
            <Switch checked disabled aria-label="Nödvändiga cookies (alltid på)" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="consent-statistics" className="text-xs font-medium">Statistik</Label>
              <p className="text-[10px] text-muted-foreground">Anonymiserad användarstatistik (Plausible).</p>
            </div>
            <Switch
              id="consent-statistics"
              checked={statistics}
              onCheckedChange={setStatistics}
              data-testid="consent-toggle-statistics"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="consent-marketing" className="text-xs font-medium">Marknadsföring</Label>
              <p className="text-[10px] text-muted-foreground">Riktade annonser och remarketing.</p>
            </div>
            <Switch
              id="consent-marketing"
              checked={marketing}
              onCheckedChange={setMarketing}
              data-testid="consent-toggle-marketing"
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {customize ? (
          <>
            <Button size="sm" onClick={handleSavePreferences} data-testid="consent-save">
              Spara val
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCustomize(false)}>
              Tillbaka
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={handleAcceptAll} data-testid="consent-accept-all">
              Acceptera alla
            </Button>
            <Button size="sm" variant="outline" onClick={handleRejectAll} data-testid="consent-reject-all">
              Endast nödvändiga
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCustomize(true)}
              className="gap-1.5"
              data-testid="consent-customize"
            >
              <Settings className="w-3.5 h-3.5" /> Anpassa
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
